import '../CheckRequestWithdraw.scss';

import { Icon, Loading } from 'components';
import { Button, NumberUtils } from 'modules';
import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetRequestWithdrawDetailQuery } from 'services';
import { WithdrawRequestEntity } from 'services/openapi';
import { MainRouteKeys } from 'routes/enums';
import { format } from 'date-fns';
import { navigateBackOrTo } from 'utils/helpers';

type RequestWithdrawDetailProps = {
  id?: string;
  backPath?: string;
};

type DetailRowProps = {
  label: string;
  value?: React.ReactNode;
};

const displayText = (value?: string | number | null) => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return '-';
  }

  return String(value);
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';

  return format(new Date(value), 'HH:mm:ss dd/MM/yyyy');
};

const getTransactionDetailPath = (transactionCode: string) =>
  MainRouteKeys.PointTransactionDetails.replace(
    ':transactionCode',
    encodeURIComponent(transactionCode),
  );

const DetailRow = ({ label, value }: DetailRowProps) => (
  <div className='withdraw-detail-row'>
    <span>{label}</span>
    <strong>{value || '-'}</strong>
  </div>
);

const DetailSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className='withdraw-detail-section'>
    <div className='withdraw-detail-section__header'>
      <span />
      <h3>{title}</h3>
    </div>
    <div className='withdraw-detail-section__body'>{children}</div>
  </section>
);

const RequestWithdrawDetail = ({
  id: idProp,
  backPath = MainRouteKeys.CheckRequestWithdrawList,
}: RequestWithdrawDetailProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = location.state?.returnTo;
  const listPath =
    typeof returnPath === 'string' &&
    (returnPath === backPath || returnPath.startsWith(`${backPath}?`))
      ? returnPath
      : backPath;
  const { id: idParam } = useParams();
  const id = idProp || idParam || '';
  const { data, isFetching, isLoading, isError } =
    useGetRequestWithdrawDetailQuery(id, Boolean(id));
  const detail = data?.data as WithdrawRequestEntity | undefined;

  return (
    <div className='withdraw-detail-page'>
      <Loading isLoading={isLoading || isFetching}>
        {isError ? (
          <div className='withdraw-detail-empty'>
            Không thể tải chi tiết lệnh yêu cầu rút điểm
          </div>
        ) : (
          detail && (
            <>
              <div className='withdraw-detail-toolbar'>
                <Button
                  label='Trở về danh sách'
                  buttonType='primary-outline'
                  onClick={() =>
                    navigateBackOrTo(navigate, listPath, {
                      preferFallback: true,
                    })
                  }
                  icon={() => <Icon.ArrowLeft />}
                />
              </div>

              <div className='withdraw-detail-hero'>
                <div>
                  <div className='withdraw-detail-hero__eyebrow'>
                    <span>
                      <Icon.WithdrawRequest />
                    </span>
                    Lệnh yêu cầu rút điểm
                  </div>
                  <div className='withdraw-detail-hero__meta'>
                    <span>{displayText(detail.phoneNumber)}</span>
                    <span>{formatDateTime(detail.createdAt)}</span>
                  </div>
                </div>
                <div className='withdraw-detail-hero__amount'>
                  <span>{displayText(detail.status)}</span>
                  <strong>
                    {NumberUtils.toFormatNumber(Number(detail.amount || 0))}
                  </strong>
                </div>
              </div>

              <div className='withdraw-detail-grid'>
                <DetailSection title='Thông tin yêu cầu'>
                  <DetailRow
                    label='Tài khoản'
                    value={displayText(detail.phoneNumber)}
                  />
                  <DetailRow
                    label='Trạng thái'
                    value={displayText(detail.status)}
                  />
                  <DetailRow
                    label='Số tiền'
                    value={NumberUtils.toFormatNumber(
                      Number(detail.amount || 0),
                    )}
                  />
                  <DetailRow
                    label='Thời gian gửi'
                    value={formatDateTime(detail.createdAt)}
                  />
                  <DetailRow
                    label='Thời gian duyệt'
                    value={formatDateTime(detail.confirmAt)}
                  />
                  <DetailRow
                    label='Nguyên nhân từ chối'
                    value={displayText(detail.rejectReason)}
                  />
                </DetailSection>

                <DetailSection title='Thông tin ngân hàng'>
                  <DetailRow
                    label='Ngân hàng'
                    value={`${displayText(detail.bankAccount?.bank?.shortName)} - ${displayText(
                      detail.bankAccount?.bank?.name,
                    )}`}
                  />
                  <DetailRow
                    label='Mã ngân hàng'
                    value={displayText(detail.bankAccount?.bank?.code)}
                  />
                  <DetailRow
                    label='Tên tài khoản'
                    value={displayText(detail.bankAccount?.accountName)}
                  />
                  <DetailRow
                    label='Số tài khoản'
                    value={displayText(detail.bankAccount?.accountNumber)}
                  />
                  <DetailRow
                    label='Tên người nhận từ BIDV'
                    value={displayText(detail.beneficiaryNameFromBidv)}
                  />
                </DetailSection>

                <DetailSection title='Giao dịch liên quan'>
                  {detail.transactions?.length ? (
                    <div className='withdraw-detail-transactions'>
                      {detail.transactions.map((transaction) => (
                        <Link
                          key={transaction.id}
                          to={getTransactionDetailPath(
                            transaction.transactionCode,
                          )}
                          target='_blank'
                          rel='noreferrer'
                        >
                          <strong>{transaction.transactionCode}</strong>
                          <span>
                            {transaction.transactionType} - {transaction.status}
                          </span>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className='withdraw-detail-empty'>
                      Không có giao dịch liên quan
                    </div>
                  )}
                </DetailSection>

                {/* <DetailSection title='BIDV tự động'>
                  <DetailRow
                    label='Trạng thái tự động'
                    value={displayText(detail.autoTransferStatus)}
                  />
                  <DetailRow
                    label='Mã request BIDV'
                    value={displayText(detail.bidvTransferRequestId)}
                  />
                  <DetailRow
                    label='Reference No'
                    value={displayText(detail.bidvReferenceNo)}
                  />
                  <DetailRow
                    label='Batch No'
                    value={displayText(detail.bidvBatchNo)}
                  />
                  <DetailRow
                    label='Trạng thái BIDV'
                    value={displayText(detail.bidvStatus)}
                  />
                  <DetailRow
                    label='Mã lỗi'
                    value={displayText(detail.autoTransferErrorCode)}
                  />
                  <DetailRow
                    label='Mô tả lỗi'
                    value={displayText(detail.autoTransferErrorDesc)}
                  />
                </DetailSection> */}
              </div>

              {detail.transactionImageUrl && (
                <DetailSection title='Hình ảnh giao dịch'>
                  <button
                    type='button'
                    className='withdraw-detail-image'
                    onClick={() =>
                      window.open(detail.transactionImageUrl, '_blank')
                    }
                  >
                    <img
                      src={detail.transactionImageUrl}
                      alt='Hình ảnh giao dịch'
                    />
                  </button>
                </DetailSection>
              )}
            </>
          )
        )}
      </Loading>
    </div>
  );
};

export default RequestWithdrawDetail;
